import type { SignalMetadataCondition } from "@repo/app-types/entities/filterConditions";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { type FC, useContext } from "react";
import { formatMetadataFieldKey } from "../signals/signalStatsFormatters.js";
import { FilterPlaceholderButton, RemoveFilterChip } from "./filterChip.js";
import { FilterContext } from "./filterContext.js";
import type { FilterParams } from "./filterParams.js";
import {
  renderAddableMetadataField,
  useAddableMetadataFields,
} from "./signalMetadataFieldMenu.js";

export interface SignalMetadataFilterItemProps {
  params: FilterParams;
  value: readonly SignalMetadataCondition[];
  onChange: (newValue: readonly SignalMetadataCondition[]) => void;
}

export const SignalMetadataFilterItem: FC<SignalMetadataFilterItemProps> = ({
  params,
  value,
  onChange,
}) => {
  const { values } = useContext(FilterContext);
  // Drilldown sets the in-scope signal via `params.signalId` (from its URL).
  // Other pages scope it through the `signalId` filter, so fall back to the
  // FilterContext value there.
  const signalId = params.signalId ?? values.signalId ?? undefined;
  const playbookId = params.playbookId;

  // Only the drilldown (signal pinned via `params.signalId`) surfaces the
  // always-visible "add metadata field" placeholder. Other pages add metadata
  // conditions from the "+ Add filter" menu, so this chip renders only the
  // already-selected conditions there — no field list query is needed.
  const showAddPlaceholder = params.signalId != null;
  const addableFields = useAddableMetadataFields(
    showAddPlaceholder ? signalId : undefined,
    playbookId,
    value,
  );

  if (!signalId || !playbookId) return null;

  return (
    <>
      {/** Show selected metadata fields */}
      {value.map((condition) => {
        const fieldLabel = formatMetadataFieldKey(condition.fieldKey);
        return (
          <RemoveFilterChip
            key={condition.fieldId}
            ariaLabel={`${fieldLabel} filter options`}
            onRemove={() =>
              onChange(value.filter((c) => c.fieldId !== condition.fieldId))
            }
          >
            <span className="max-w-32 truncate">{fieldLabel}</span>
            <span>&nbsp;= &quot;{condition.label}&quot;</span>
          </RemoveFilterChip>
        );
      })}

      {/** Drilldown only: show metadata fields that can still be selected */}
      {showAddPlaceholder && addableFields.length > 0 ? (
        <FilterPlaceholderButton placeholder="Signal fields">
          <SimpleMenu aria-label="Filter by signal field">
            {addableFields.map((field) =>
              renderAddableMetadataField(field, value, onChange),
            )}
          </SimpleMenu>
        </FilterPlaceholderButton>
      ) : null}
    </>
  );
};
